
import { Lead, ContextProfile, CallTranscript, AISummary } from "@/types";
import { generateContextProfile, getContextProfileByLeadId } from "./contextProfileService";
import { transcribeCall, getCallTranscriptByLeadId } from "./callTranscriptService";
import { generateAISummary, getAISummaryByLeadId } from "./aiSummaryService";
import { startRealCampaign } from "@/services/campaigns";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Tables } from "@/integrations/supabase/types";

// Process a lead through the complete AI call flow
export const processLeadCallFlow = async (lead: Lead, voiceId?: string): Promise<{
  success: boolean;
  message?: string;
  error?: string;
}> => {
  try {
    if (!lead.id) {
      return { success: false, error: "Lead ID is required" };
    }

    // Step 1: Generate or retrieve context profile
    console.log(`Starting AI call flow for lead: ${lead.name}`);
    let contextProfile = await getContextProfileByLeadId(lead.id);
    
    if (!contextProfile) {
      console.log("No existing context profile found, generating new one...");
      // The generateContextProfile function is defined to return our custom ContextProfile type
      // but we need to adapt it to match the Supabase table structure
      const generatedProfile = await generateContextProfile(lead);
      
      // Convert our custom type to match what Supabase expects
      contextProfile = {
        id: generatedProfile.id,
        lead_id: generatedProfile.lead_id,
        tone: generatedProfile.tone || null,
        summary: generatedProfile.summary || null,
        vehicle_interest: generatedProfile.vehicle_interest || null,
        next_best_action: generatedProfile.next_best_action || null,
        ai_model: generatedProfile.ai_model,
        created_at: new Date().toISOString()
      };
    }

    // Step 2: Use the context profile to personalize a script and make the call
    console.log("Initiating personalized call with context...");
    
    // Personalize script based on context profile
    const scriptTemplate = `Hello, this is ${lead.name ? lead.name : "a potential customer"} from the dealership. 
    I noticed you were interested in ${contextProfile.vehicle_interest || "our vehicles"} recently. 
    ${contextProfile.next_best_action || "I wanted to follow up and see if you're still interested."}`;
    
    // Make the actual call
    const callResult = await startRealCampaign([lead], scriptTemplate, voiceId);
    
    if (!callResult.success) {
      throw new Error(callResult.error || "Failed to initiate call");
    }

    // Update lead status
    await supabase
      .from("leads")
      .update({ status: "in-progress" })
      .eq("id", lead.id);

    // In a real implementation, we would wait for a webhook callback
    // For now, we'll simulate a successful call with a transcript
    
    // Step 3: Get or create call transcript (in production, this would be triggered by a webhook)
    console.log("Call completed, retrieving transcript...");
    // For demo purposes, we'll check if there's an existing transcript or create a placeholder
    let callTranscript = await getCallTranscriptByLeadId(lead.id);
    
    if (!callTranscript) {
      // In production, this would come from the real call
      const generatedTranscript = await transcribeCall(lead.id, "https://example.com/simulated-call-audio.mp3");
      
      // Convert our custom type to match what Supabase expects
      callTranscript = {
        id: generatedTranscript.id,
        lead_id: generatedTranscript.lead_id,
        audio_url: generatedTranscript.audio_url,
        transcript: generatedTranscript.transcript || null,
        created_at: new Date().toISOString()
      };
    }

    // Step 4: Generate AI summary from transcript
    console.log("Generating AI summary from transcript...");
    let aiSummary = await getAISummaryByLeadId(lead.id);
    
    if (!aiSummary && callTranscript.transcript) {
      const generatedSummary = await generateAISummary(lead.id, callTranscript.transcript);
      
      // Convert our custom type to match what Supabase expects
      aiSummary = {
        id: generatedSummary.id,
        lead_id: generatedSummary.lead_id,
        summary: generatedSummary.summary || null,
        intent_score: generatedSummary.intent_score,
        next_action: generatedSummary.next_action || null,
        model_used: generatedSummary.model_used,
        created_at: new Date().toISOString()
      };
    }

    // Step 5: Update lead status based on AI summary
    if (aiSummary) {
      const newStatus = aiSummary.intent_score?.toLowerCase().includes("hot") || 
                      aiSummary.intent_score?.toLowerCase().includes("warm") ? 
                      "reactivated" : "not-interested";
      
      await supabase
        .from("leads")
        .update({ status: newStatus })
        .eq("id", lead.id);
    }

    return { 
      success: true, 
      message: "Lead successfully processed through AI call flow" 
    };
  } catch (error) {
    console.error("Error in call flow process:", error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : "An unknown error occurred during the call flow process" 
    };
  }
};

// Get all call flow data for a lead
export const getLeadCallFlowData = async (leadId: string): Promise<{
  contextProfile?: ContextProfile | null;
  callTranscript?: CallTranscript | null;
  aiSummary?: AISummary | null;
}> => {
  try {
    // Use Promise.all to run all queries in parallel
    const [contextProfile, callTranscript, aiSummary] = await Promise.all([
      getContextProfileByLeadId(leadId),
      getCallTranscriptByLeadId(leadId),
      getAISummaryByLeadId(leadId)
    ]);

    return {
      contextProfile: contextProfile || undefined,
      callTranscript: callTranscript || undefined,
      aiSummary: aiSummary || undefined
    };
  } catch (error) {
    console.error("Error fetching call flow data:", error);
    toast({
      title: "Error",
      description: "Failed to load call flow data for this lead",
      variant: "destructive"
    });
    return {};
  }
};
